/**
 * Client-side-only light/dark toggle (Navbar toggle).
 *
 * This is a pure per-device override layered ON TOP of the admin-configured
 * `publicTheme` setting — it never touches the backend, and applies to
 * every viewer, authenticated or not. Persisted in
 * localStorage so the visitor's choice survives future visits; absent by
 * default, in which case `publicTheme` is used unchanged.
 *
 * "Light" maps to the newspaper theme, "dark" to the site's dark/teal
 * theme (theme id `'dark'`) — the only two options the visible toggle
 * exposes, regardless of how many themes are registered overall.
 */

export type PublicThemeOverrideMode = 'light' | 'dark'

const STORAGE_KEY = 'public-theme-override'

export const LIGHT_THEME_ID = 'newspaper'
export const DARK_THEME_ID = 'dark'

function isOverrideMode(value: unknown): value is PublicThemeOverrideMode {
  return value === 'light' || value === 'dark'
}

/** Reads the visitor's stored override, or `null` if none is set / storage
 * is unavailable (private browsing, disabled storage, etc. — never throws). */
export function getPublicThemeOverride(): PublicThemeOverrideMode | null {
  try {
    const stored = window.localStorage.getItem(STORAGE_KEY)
    return isOverrideMode(stored) ? stored : null
  } catch {
    return null
  }
}

/** Persists the visitor's choice and notifies same-tab listeners — the
 * native `storage` event only fires in OTHER tabs, so a dedicated
 * same-window CustomEvent is dispatched too (see `subscribeToPublicThemeOverride`). */
export function setPublicThemeOverride(mode: PublicThemeOverrideMode): void {
  try {
    window.localStorage.setItem(STORAGE_KEY, mode)
  } catch {
    // Storage unavailable (private browsing, quota, etc.) — the toggle still
    // updates the in-memory theme for this page load via the dispatched
    // event below, it just won't persist across visits.
  }
  window.dispatchEvent(new CustomEvent(PUBLIC_THEME_OVERRIDE_EVENT))
}

export const PUBLIC_THEME_OVERRIDE_EVENT = 'public-theme-override-change'

/** Maps an override mode to the concrete theme id it forces. */
export function themeIdForOverride(mode: PublicThemeOverrideMode): string {
  return mode === 'light' ? LIGHT_THEME_ID : DARK_THEME_ID
}

/**
 * Subscribes to both the same-tab custom event (fired by
 * `setPublicThemeOverride`) and the cross-tab native `storage` event, so a
 * toggle click in one tab/component is reflected everywhere immediately.
 * Returns an unsubscribe function.
 */
export function subscribeToPublicThemeOverride(callback: () => void): () => void {
  const handleStorage = (event: StorageEvent) => {
    if (event.key === STORAGE_KEY || event.key === null) callback()
  }
  window.addEventListener(PUBLIC_THEME_OVERRIDE_EVENT, callback)
  window.addEventListener('storage', handleStorage)
  return () => {
    window.removeEventListener(PUBLIC_THEME_OVERRIDE_EVENT, callback)
    window.removeEventListener('storage', handleStorage)
  }
}
